import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { globalSearch } from '../lib/api';
import useDebounce from '../hooks/useDebounce';
import { MagnifyingGlassIcon, FolderIcon, ServerStackIcon, UserIcon } from '@heroicons/react/24/outline';

const typeIcons = {
  project: FolderIcon,
  environment: ServerStackIcon,
  client: UserIcon,
};

const typeColors = {
  project: 'bg-blue-500/10 text-blue-400',
  environment: 'bg-green-500/10 text-green-400',
  client: 'bg-purple-500/10 text-purple-400',
};

export default function GlobalSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const debouncedQuery = useDebounce(query, 200);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (debouncedQuery.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    globalSearch(debouncedQuery)
      .then((res) => setResults(res.data || []))
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, [debouncedQuery]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleKeydown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === 'Escape') {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, []);

  const handleSelect = (result) => {
    setOpen(false);
    setQuery('');
    navigate(result.url);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && selectedIndex >= 0 && results[selectedIndex]) {
      e.preventDefault();
      handleSelect(results[selectedIndex]);
    }
  };

  return (
    <div ref={wrapperRef} className="relative hidden sm:block">
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-blue-200/30" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setSelectedIndex(-1); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search... (Ctrl+K)"
          className="w-64 pl-9 pr-3 py-1.5 text-sm rounded-lg border border-blue-500/15 bg-white/5 text-white placeholder-blue-200/30 focus:bg-white/10 focus:border-[#5ba4f5]/50 focus:ring-1 focus:ring-[#5ba4f5]/50 focus:outline-none transition-colors"
        />
      </div>

      {open && (query.length >= 2 || results.length > 0) && (
        <div className="absolute top-full mt-1 left-0 w-80 bg-[#0f1535] rounded-xl shadow-lg border border-white/10 overflow-hidden z-50">
          {loading ? (
            <div className="p-4 text-center text-sm text-blue-200/40">Searching...</div>
          ) : results.length === 0 ? (
            <div className="p-4 text-center text-sm text-blue-200/40">No results found</div>
          ) : (
            <div className="max-h-72 overflow-y-auto">
              {results.map((r, idx) => {
                const Icon = typeIcons[r.type] || FolderIcon;
                return (
                  <button
                    key={`${r.type}-${r.id}`}
                    onClick={() => handleSelect(r)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-white/5 transition-colors ${
                      idx === selectedIndex ? 'bg-white/5' : ''
                    }`}
                  >
                    <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${typeColors[r.type] || 'bg-white/5 text-blue-200/60'}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-white truncate">{r.label}</p>
                      <p className="text-xs text-blue-200/40 capitalize">{r.type}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
